import { useState, useEffect, useCallback } from 'react';
import { orgApi } from '@/lib/api/org';
import { useOrgStore } from '@/store/orgStore';
import type { OrganizationMember, OrganizationRole, Permission } from '@/types/org';
import { hasPermission as checkPermission } from '@/types/org';

interface UsePermissionsResult {
  /** Current user's membership for the selected organization */
  membership: OrganizationMember | null;
  /** Current user's role in the organization (null if not a member) */
  role: OrganizationRole | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if fetching failed */
  error: string | null;
  /** Check if current user has a specific permission */
  hasPermission: (permission: Permission) => boolean;
  /** Check if current user has any of the specified permissions */
  hasAnyPermission: (permissions: Permission[]) => boolean;
  /** Check if current user has all of the specified permissions */
  hasAllPermissions: (permissions: Permission[]) => boolean;
  /** Check if current user is an owner */
  isOwner: boolean;
  /** Check if current user is an admin */
  isAdmin: boolean;
  /** Check if current user is a member (not viewer) */
  isMember: boolean;
  /** Check if current user is a viewer */
  isViewer: boolean;
  /** Refetch the membership data */
  refetch: () => Promise<void>;
}

/**
 * Hook to manage organization-level permissions for the current user.
 *
 * Fetches the current user's membership for the selected organization
 * and provides convenience methods for checking permissions.
 *
 * @example
 * ```tsx
 * function SettingsPage() {
 *   const { hasPermission, isLoading } = usePermissions();
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {hasPermission('org:modify_settings') && (
 *         <button>Edit Settings</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const { currentOrg } = useOrgStore();
  const [membership, setMembership] = useState<OrganizationMember | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembership = useCallback(async () => {
    if (!currentOrg?.id) {
      setMembership(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await orgApi.getMyMembership(currentOrg.id);
      setMembership(data);
    } catch (err: unknown) {
      // 404 means user is not a member - this is expected for some users
      const error = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      if (error.response?.status === 404) {
        setMembership(null);
        setError(null);
      } else {
        setError(error.response?.data?.detail || error.message || 'Failed to fetch membership');
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentOrg?.id]);

  // Fetch membership when organization changes
  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  // Extract role from membership
  const role = membership?.role ?? null;

  // Permission checking functions using useMemo to avoid recreating on every render
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!role) return false;
      return checkPermission(role, permission);
    },
    [role]
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      if (!role) return false;
      return permissions.some((p) => checkPermission(role, p));
    },
    [role]
  );

  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      if (!role) return false;
      return permissions.every((p) => checkPermission(role, p));
    },
    [role]
  );

  // Convenience boolean flags
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin' || role === 'owner';
  const isMember = role === 'member';
  const isViewer = role === 'viewer';

  return {
    membership,
    role,
    isLoading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOwner,
    isAdmin,
    isMember,
    isViewer,
    refetch: fetchMembership,
  };
}

/**
 * Get the display name for a role
 */
export function getRoleDisplayName(role: OrganizationRole): string {
  const names: Record<OrganizationRole, string> = {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
    viewer: 'Viewer',
  };
  return names[role] || role;
}

/**
 * Get the role color for styling
 */
export function getRoleColor(role: OrganizationRole): string {
  const colors: Record<OrganizationRole, string> = {
    owner: 'purple',
    admin: 'purple',
    member: 'blue',
    viewer: 'gray',
  };
  return colors[role] || 'gray';
}

/**
 * Get role badge classes for Tailwind
 */
export function getRoleBadgeClasses(role: OrganizationRole): string {
  const classes: Record<OrganizationRole, string> = {
    owner: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    member: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    viewer: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };
  return classes[role] || 'bg-gray-100 text-gray-700';
}
